import React, { useState, useEffect } from 'react';
import { Check as CheckIcon } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import produce from 'immer';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AxlButton } from '../../components/AxlButton';
import { getClient, listTelephone, updateTelephones } from '../../repositories/HttpRepository';
import { showToastError } from '../../utils/toastUtil';

import Description from './Description';
const useStyles = makeStyles()((theme) => ({
  dialogAction: {
    padding: '8px 24px',
  },
  header: {},
  checkBox: {
    height: '32px',
    minWidth: '32px',
    padding: '0 4px',
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  description: {
    marginTop: '20px',
    padding: '8px 24px',
  },
}));
const EditTelephone = () => {
  const { clientId } = useParams();
  const [telephone, setTelephone] = useState([]);
  const [savedTelephone, setSavedTelephone] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const clientNameFromState = useLocation().state?.clientName ?? '';
  const [fetchedClientName, setFetchedClientName] = useState('');
  const clientName = clientNameFromState || fetchedClientName;
  const navigate = useNavigate();
  const { classes } = useStyles();
  useEffect(() => {
    listTelephone(clientId).then((resp) => {
      if (resp.ok) {
        setTelephone(resp.data);
        setSavedTelephone(resp.data);
      }
    });
  }, []);
  useEffect(() => {
    // only fetch when the client name was not passed along from the client list
    if (clientNameFromState) {
      return;
    }
    getClient(clientId, false).then((resp) => {
      if (resp.ok) {
        setFetchedClientName(resp.data?.client?.name ?? '');
      }
    });
  }, [clientId, clientNameFromState]);
  const handleChangeCheckBox = (value, index) => {
    setTelephone(
      produce(telephone, (draft) => {
        draft[index].sms_activated = value;
      }),
    );
  };
  const changeInputMacro = (e, index) => {
    setTelephone(
      produce(telephone, (draft) => {
        draft[index].sms_macro = e.target.value;
      }),
    );
  };

  const hasChanges = telephone.some((item, index) => {
    const saved = savedTelephone[index];
    return !saved || saved.sms_macro !== item.sms_macro || saved.sms_activated !== item.sms_activated;
  });

  const updateAction = async () => {
    setIsUpdating(true);
    try {
      const payload = telephone.map((item) => ({ sms_type: item.sms_type, sms_macro: item.sms_macro, sms_activated: item.sms_activated }));
      const resp = await updateTelephones(clientId, payload);
      if (!resp.ok) {
        showToastError(resp);
        return;
      }
      toast.success('The telephony config was updated successfully.');
      setSavedTelephone(telephone);
    } catch (error) {
      showToastError(error?.response);
    } finally {
      setIsUpdating(false);
    }
  };
  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open
      onClose={() => {
        navigate('/');
      }}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        <Typography variant="h4">{`Update Telephone - [${clientId}]${clientName ? ` ${clientName}` : ''}`}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Paper className={classes.paper}>
          <TableContainer>
            <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '30%' }}>SMS Type</TableCell>
                  <TableCell style={{ width: '10%' }}>Is Active</TableCell>
                  <TableCell style={{ width: '60%' }}>SMS Macro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {telephone.length > 0 &&
                  telephone.map((item, index) => {
                    const labelId = `telephone-table-item-${index}`;
                    return (
                      <TableRow hover tabIndex={-1} key={item.sms_type}>
                        <TableCell component="th" id={labelId} scope="row">
                          {item.sms_type}
                        </TableCell>
                        <TableCell>
                          <ToggleButton
                            value="check"
                            className={classes.checkBox}
                            selected={telephone[index].sms_activated}
                            onChange={() => {
                              handleChangeCheckBox(!telephone[index].sms_activated, index);
                            }}
                          >
                            {telephone[index]?.sms_activated && <CheckIcon />}
                          </ToggleButton>
                          {item.smsActivated}
                        </TableCell>
                        <TableCell>
                          <TextField variant="outlined" multiline fullWidth value={telephone[index]?.sms_macro ?? ''} onChange={(e) => changeInputMacro(e, index)} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>
      <DialogActions className={classes.dialogAction}>
        <Description sx={{ mr: 'auto', mt: 0, p: 0 }} placement="top-start" />
        <AxlButton
          onClick={() => {
            navigate('/');
          }}
          color="cancel"
          variant="outlined"
        >
          Cancel
        </AxlButton>
        <AxlButton
          disabled={!hasChanges}
          loading={isUpdating}
          onClick={() => {
            updateAction();
          }}
          variant="contained"
          color="primary"
        >
          Update
        </AxlButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(EditTelephone);
